import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Customer,
  CustomerPaginatedResponse,
} from "../../types/pages/customer";

interface CustomersState {
  customers: Customer[];
  loading: boolean;
  error: string | null;

  selectedCustomer: Customer | null;
  fetchCustomerLoading: boolean;
  fetchCustomerError: string | null;
  isCustomerFetched: boolean;

  editCustomerLoading: boolean;
  editCustomerSuccess: boolean;
  editCustomerError: string | null;

  deleteCustomerLoading: boolean;
  deleteCustomerSuccess: boolean;
  deleteCustomerError: string | null;

  scannedProducts: any[];
  scannedProductsLoading: boolean;
  scannedProductsError: string | null;

  totalCount: number;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: number;
}

const initialState: CustomersState = {
  customers: [],
  loading: false,
  error: null,

  selectedCustomer: null,
  fetchCustomerLoading: false,
  fetchCustomerError: null,
  isCustomerFetched: false,

  editCustomerLoading: false,
  editCustomerSuccess: false,
  editCustomerError: null,

  deleteCustomerLoading: false,
  deleteCustomerSuccess: false,
  deleteCustomerError: null,

  scannedProducts: [],
  scannedProductsLoading: false,
  scannedProductsError: null,

  totalCount: 0,
  page: 0,
  rowsPerPage: 10,
  sortBy: "first_name",
  sortOrder: 1,
};

const customersSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    // LIST
    fetchCustomers: (
      state,
      action: PayloadAction<{
        page: number;
        rowsPerPage: number;
        sortBy?: string;
        sortOrder?: number;
      }>,
    ) => {},

    fetchCustomersStart: (state) => {
      state.loading = true;
      state.error = null;
    },

    fetchCustomersSuccess: (
      state,
      action: PayloadAction<CustomerPaginatedResponse>,
    ) => {
      state.loading = false;
      state.customers = action.payload.data;
      state.totalCount = action.payload.totalCount;
    },

    fetchCustomersFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.rowsPerPage = action.payload;
      state.page = 0;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
      state.page = 0;
    },
    setSortOrder: (state, action: PayloadAction<number>) => {
      state.sortOrder = action.payload;
      state.page = 0;
    },

    // SINGLE
    fetchCustomerById: (state, action: PayloadAction<string>) => {},

    fetchCustomerByIdStart: (state) => {
      state.fetchCustomerLoading = true;
      state.fetchCustomerError = null;
      state.selectedCustomer = null;
      state.isCustomerFetched = false; // 👈 reset
    },

    fetchCustomerByIdSuccess: (state, action: PayloadAction<Customer>) => {
      state.fetchCustomerLoading = false;
      state.selectedCustomer = action.payload;
      state.isCustomerFetched = true;
    },

    fetchCustomerByIdFailure: (state, action: PayloadAction<string>) => {
      state.fetchCustomerLoading = false;
      state.fetchCustomerError = action.payload;
      state.isCustomerFetched = true;
    },

    // EDIT
    editCustomer: (
      state,
      action: PayloadAction<{
        customerId: string;
        customerData: Partial<Customer>;
      }>,
    ) => {},

    editCustomerStart: (state) => {
      state.editCustomerLoading = true;
      state.editCustomerError = null;
      state.editCustomerSuccess = false;
    },

    editCustomerSuccess: (state, action: PayloadAction<Customer>) => {
      state.editCustomerLoading = false;
      const index = state.customers.findIndex(
        (c) => c.email === action.payload._id,
      );
      if (index !== -1) {
        state.customers[index] = action.payload;
      }
      if (state.selectedCustomer?._id === action.payload._id) {
        state.selectedCustomer = action.payload;
      }
      state.editCustomerSuccess = true;
    },
    editResetCustomerState: (state) => {
      state.editCustomerSuccess = false;
      state.editCustomerError = null;
    },

    editCustomerFailure: (state, action: PayloadAction<string>) => {
      state.editCustomerLoading = false;
      state.editCustomerError = action.payload;
      state.editCustomerSuccess = false;
    },

    // DELETE
    deleteCustomer: (state, action: PayloadAction<string>) => {},

    deleteCustomerStart: (state) => {
      state.deleteCustomerLoading = true;
      state.deleteCustomerError = null;
      state.deleteCustomerSuccess = false;
    },

    deleteCustomerSuccess: (state, action: PayloadAction<string>) => {
      state.deleteCustomerLoading = false;
      state.customers = state.customers.filter((c) => c._id !== action.payload);
      if (state.selectedCustomer?._id === action.payload) {
        state.selectedCustomer = null;
      }
      state.deleteCustomerSuccess = true;
    },
    deleteResetCustomer: (state) => {
      state.deleteCustomerSuccess = false;
      state.deleteCustomerError = null;
    },
    deleteCustomerFailure: (state, action: PayloadAction<string>) => {
      state.deleteCustomerLoading = false;
      state.deleteCustomerError = action.payload;
      state.deleteCustomerSuccess = false;
    },

    // SCANS
    fetchCustomerScans: (state, action: PayloadAction<string>) => {},

    fetchCustomerScansStart: (state) => {
      state.scannedProductsLoading = true;
      state.scannedProductsError = null;
    },

    fetchCustomerScansSuccess: (state, action: PayloadAction<any[]>) => {
      state.scannedProductsLoading = false;
      state.scannedProducts = action.payload;
    },

    fetchCustomerScansFailure: (state, action: PayloadAction<string>) => {
      state.scannedProductsLoading = false;
      state.scannedProductsError = action.payload;
    },
    clearSelectedCustomer: (state) => {
      state.selectedCustomer = null;
      state.fetchCustomerError = null;
      state.isCustomerFetched = false;
      state.fetchCustomerLoading = false;
    },
  },
});

export const {
  fetchCustomers,
  fetchCustomersStart,
  fetchCustomersSuccess,
  fetchCustomersFailure,

  fetchCustomerById,
  fetchCustomerByIdStart,
  fetchCustomerByIdSuccess,
  fetchCustomerByIdFailure,

  editCustomer,
  editCustomerStart,
  editCustomerSuccess,
  editCustomerFailure,
  editResetCustomerState,

  deleteCustomer,
  deleteCustomerStart,
  deleteCustomerSuccess,
  deleteCustomerFailure,
  deleteResetCustomer,

  fetchCustomerScans,
  fetchCustomerScansStart,
  fetchCustomerScansSuccess,
  fetchCustomerScansFailure,
  clearSelectedCustomer,

  setPage,
  setRowsPerPage,
  setSortBy,
  setSortOrder,
} = customersSlice.actions;

export default customersSlice.reducer;
